import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CreditCard,
  Plus,
  Eye,
  RotateCcw,
  X,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Paperclip,
  Upload,
  Loader2,
  Trash2,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import {
  paymentsApi,
  invoicesApi,
  PaginatedResponse,
} from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import ConfirmDialog from "../ui/ConfirmDialog";
import { useUserDataContext } from "../../context/UserDataContext";
import InlineNotification, { NotificationState } from "../ui/InlineNotification";

interface Payment {
  _id: string;
  invoiceId: any;
  matterId: any;
  clientId: any;
  source: string;
  status: string;
  amountCents: number;
  currency: string;
  receivedAt: string;
  receivedBy: any;
  methodDetails?: {
    chequeNumber?: string;
    eftReference?: string;
    receivedFrom?: string;
    notes?: string;
  };
  allocation?: {
    appliedToInvoiceCents: number;
    appliedToTrustCents: number;
  };
  supportingDocumentIds?: any[];
}

interface Props {
  onDataChange?: () => void;
}

const formatCurrency = (cents: number, currency = "CAD") =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(cents / 100);

const sourceColors: Record<string, string> = {
  stripe: "bg-purple-100 text-purple-700",
  cheque: "bg-blue-100 text-blue-700",
  eft: "bg-cyan-100 text-cyan-700",
  cash: "bg-emerald-100 text-emerald-700",
  trust_transfer: "bg-amber-100 text-amber-700",
};

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  succeeded: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-600",
  refunded: "bg-slate-100 text-slate-600",
  partially_refunded: "bg-amber-100 text-amber-700",
};

const formatSource = (s: string) =>
  s
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const PaymentsTab: React.FC<Props> = ({ onDataChange }) => {
  const { canManageBilling } = useUserDataContext();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterSource, setFilterSource] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [createForm, setCreateForm] = useState({
    invoiceId: "",
    source: "cheque" as string,
    amountCents: 0,
    chequeNumber: "",
    eftReference: "",
    receivedFrom: "",
    notes: "",
  });

  // Detail
  const [detailPayment, setDetailPayment] = useState<Payment | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Notification & confirmation
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [confirmState, setConfirmState] = useState<{ open: boolean; paymentId: string }>({ open: false, paymentId: "" });

  // Receipt attachment
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [removingDocId, setRemovingDocId] = useState<string | null>(null);
  const receiptFileRef = React.useRef<HTMLInputElement>(null);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (filterSource) params.source = filterSource;
      if (filterStatus) params.status = filterStatus;

      const res = await paymentsApi.list(params);
      const data = res.data as PaginatedResponse<Payment>;
      setPayments(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch payments:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterSource, filterStatus]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const loadInvoices = async () => {
    try {
      const [issuedRes, partialRes] = await Promise.all([
        invoicesApi.list({ status: "issued", limit: 200 }),
        invoicesApi.list({ status: "partial", limit: 200 }),
      ]);
      setInvoices([...(issuedRes.data?.data || []), ...(partialRes.data?.data || [])]);
    } catch (err) {
      console.error("Failed to load invoices:", err);
    }
  };

  const viewPayment = async (id: string) => {
    try {
      const res = await paymentsApi.get(id);
      setDetailPayment(res.data);
      setShowDetail(true);
    } catch (err) {
      console.error("Failed to fetch payment:", err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const paymentRes = await paymentsApi.create({
        invoiceId: createForm.invoiceId,
        source: createForm.source,
        amountCents: createForm.amountCents,
        methodDetails: {
          chequeNumber: createForm.chequeNumber || null,
          eftReference: createForm.eftReference || null,
          receivedFrom: createForm.receivedFrom || null,
          notes: createForm.notes || null,
        },
      });

      // Upload receipt if one was attached
      if (receiptFile && paymentRes.data?._id) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        await paymentsApi.addAttachment(paymentRes.data._id, fd);
      }

      setReceiptFile(null);
      setShowCreateModal(false);
      fetchPayments();
      onDataChange?.();
      setNotification({ type: "success", message: "Payment recorded successfully" });
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.message || err.response?.data?.error || "Failed to record payment" });
    } finally {
      setCreating(false);
    }
  };

  const handleUploadReceiptToExisting = async (paymentId: string, file: File) => {
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await paymentsApi.addAttachment(paymentId, fd);
      // Refresh detail
      const res = await paymentsApi.get(paymentId);
      setDetailPayment(res.data);
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to upload receipt" });
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleRemoveReceipt = async (paymentId: string, docId: string) => {
    setRemovingDocId(docId);
    try {
      await paymentsApi.removeAttachment(paymentId, docId);
      const res = await paymentsApi.get(paymentId);
      setDetailPayment(res.data);
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to remove receipt" });
    } finally {
      setRemovingDocId(null);
    }
  };

  const handleRefundRequest = (id: string) => {
    setConfirmState({ open: true, paymentId: id });
  };

  const handleRefundConfirmed = async () => {
    const id = confirmState.paymentId;
    setConfirmState({ open: false, paymentId: "" });
    try {
      await paymentsApi.refund(id);
      fetchPayments();
      onDataChange?.();
      if (showDetail) {
        const res = await paymentsApi.get(id);
        setDetailPayment(res.data);
      }
      setNotification({ type: "success", message: "Payment refunded successfully" });
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.message || err.response?.data?.error || "Failed to refund" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      <InlineNotification notification={notification} onDismiss={() => setNotification(null)} />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmState.open}
        title="Refund Payment"
        message="Are you sure you want to refund this payment? This will reverse the invoice amounts."
        confirmLabel="Refund Payment"
        variant="danger"
        onConfirm={handleRefundConfirmed}
        onCancel={() => setConfirmState({ open: false, paymentId: "" })}
      />

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        {canManageBilling && (
        <button
          onClick={() => {
            setCreateForm({
              invoiceId: "",
              source: "cheque",
              amountCents: 0,
              chequeNumber: "",
              eftReference: "",
              receivedFrom: "",
              notes: "",
            });
            loadInvoices();
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <Plus className="h-4 w-4" />
          Record Payment
        </button>
        )}
        {!canManageBilling && (
          <p className="text-sm text-slate-500">
            Recording payments requires billing permission. Ask your firm admin.
          </p>
        )}
        <p className="text-sm text-slate-500">{pagination.total} payments</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <SearchableSelect
            options={[
              { value: "stripe", label: "Stripe" },
              { value: "cheque", label: "Cheque" },
              { value: "eft", label: "EFT" },
              { value: "cash", label: "Cash" },
              { value: "trust_transfer", label: "Trust Transfer" },
            ]}
            value={filterSource || null}
            onChange={(val) => {
              setFilterSource(val || "");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="All Sources"
            clearable
          />
          <SearchableSelect
            options={[
              { value: "pending", label: "Pending" },
              { value: "succeeded", label: "Succeeded" },
              { value: "failed", label: "Failed" },
              { value: "refunded", label: "Refunded" },
            ]}
            value={filterStatus || null}
            onChange={(val) => {
              setFilterStatus(val || "");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="All Statuses"
            clearable
          />
        </div>
      </div>

      {/* Payment List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : payments.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20"
        >
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
            <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
              <CreditCard className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No payments yet</h3>
          <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
            {canManageBilling
              ? "Record payments received against your invoices. Track cheques, EFT transfers, cash, and Stripe payments all in one place."
              : "Payments received against your firm's invoices will appear here. Recording payments requires billing permission. Ask your firm admin."}
          </p>
          {canManageBilling && (
          <button
            onClick={() => {
              setCreateForm({
                invoiceId: "",
                source: "cheque",
                amountCents: 0,
                chequeNumber: "",
                eftReference: "",
                receivedFrom: "",
                notes: "",
              });
              loadInvoices();
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Plus className="h-4 w-4" />
            Record Payment
          </button>
          )}
        </motion.div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                    Date
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                    Invoice
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">
                    Client
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                    Source
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <motion.tr
                    key={p._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(p.receivedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-slate-600 hidden md:table-cell">
                      {typeof p.invoiceId === "object" ? p.invoiceId?.invoiceNumber : "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell">
                      {typeof p.clientId === "object" ? p.clientId?.displayName : "-"}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          sourceColors[p.source] || "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {formatSource(p.source)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          statusColors[p.status] || "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {formatSource(p.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-800 text-right">
                      {formatCurrency(p.amountCents)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => viewPayment(p._id)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {canManageBilling && p.status === "succeeded" && (
                          <button
                            onClick={() => handleRefundRequest(p._id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"
                            title="Refund"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.pages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Detail Drawer */}
      <AnimatePresence>
        {showDetail && detailPayment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex justify-end"
            onClick={() => setShowDetail(false)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-800">Payment Details</h2>
                  <button
                    onClick={() => setShowDetail(false)}
                    className="p-2 rounded-lg hover:bg-slate-100"
                  >
                    <X className="h-5 w-5 text-slate-500" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-slate-800">
                      {formatCurrency(detailPayment.amountCents)}
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          sourceColors[detailPayment.source] || "bg-slate-100"
                        }`}
                      >
                        {formatSource(detailPayment.source)}
                      </span>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          statusColors[detailPayment.status] || "bg-slate-100"
                        }`}
                      >
                        {formatSource(detailPayment.status)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Invoice</p>
                      <p className="text-sm font-medium text-slate-800">
                        {typeof detailPayment.invoiceId === "object"
                          ? detailPayment.invoiceId?.invoiceNumber
                          : "-"}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Client</p>
                      <p className="text-sm font-medium text-slate-800">
                        {typeof detailPayment.clientId === "object"
                          ? detailPayment.clientId?.displayName
                          : "-"}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Received At</p>
                      <p className="text-sm font-medium text-slate-800">
                        {new Date(detailPayment.receivedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Received By</p>
                      <p className="text-sm font-medium text-slate-800">
                        {typeof detailPayment.receivedBy === "object"
                          ? detailPayment.receivedBy?.name
                          : "-"}
                      </p>
                    </div>
                  </div>

                  {/* Method Details */}
                  {detailPayment.methodDetails && (
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500 mb-2">Method Details</p>
                      {detailPayment.methodDetails.chequeNumber && (
                        <p className="text-sm text-slate-700">
                          Cheque #: {detailPayment.methodDetails.chequeNumber}
                        </p>
                      )}
                      {detailPayment.methodDetails.eftReference && (
                        <p className="text-sm text-slate-700">
                          EFT Ref: {detailPayment.methodDetails.eftReference}
                        </p>
                      )}
                      {detailPayment.methodDetails.receivedFrom && (
                        <p className="text-sm text-slate-700">
                          From: {detailPayment.methodDetails.receivedFrom}
                        </p>
                      )}
                      {detailPayment.methodDetails.notes && (
                        <p className="text-sm text-slate-700">
                          Notes: {detailPayment.methodDetails.notes}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Allocation */}
                  {detailPayment.allocation && (
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500 mb-2">Allocation</p>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Applied to Invoice</span>
                        <span className="font-medium text-slate-800">
                          {formatCurrency(detailPayment.allocation.appliedToInvoiceCents)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-slate-600">Applied to Trust</span>
                        <span className="font-medium text-slate-800">
                          {formatCurrency(detailPayment.allocation.appliedToTrustCents)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Receipts / Supporting Documents */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-500 font-medium">Receipts</p>
                      <label className="flex items-center gap-1 text-xs font-medium cursor-pointer hover:text-brand transition-colors" style={{ color: "var(--color-primary)" }}>
                        {uploadingReceipt ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Upload className="h-3 w-3" />
                        )}
                        Attach
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleUploadReceiptToExisting(detailPayment._id, f);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                    {(detailPayment.supportingDocumentIds?.length || 0) > 0 ? (
                      <div className="space-y-2">
                        {detailPayment.supportingDocumentIds!.map((doc: any) => {
                          const docObj = typeof doc === "object" ? doc : null;
                          const docId = docObj?._id || doc;
                          const title = docObj?.title || "Receipt";
                          const mime = docObj?.currentVersionId?.file?.mimeType || "";
                          const isImage = mime.startsWith("image/");
                          return (
                            <div key={docId} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-200">
                              {isImage ? (
                                <ImageIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              ) : (
                                <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                              )}
                              <span className="text-sm text-slate-700 truncate flex-1">{title}</span>
                              <button
                                onClick={() => handleRemoveReceipt(detailPayment._id, docId)}
                                disabled={removingDocId === docId}
                                className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                title="Remove"
                              >
                                {removingDocId === docId ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No receipts attached</p>
                    )}
                  </div>

                  {/* Actions */}
                  {canManageBilling && detailPayment.status === "succeeded" && (
                    <button
                      onClick={() => handleRefundRequest(detailPayment._id)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm transition-colors"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Refund Payment
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Record Payment Modal */}
      <PremiumModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Record Payment"
        icon={DollarSign}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="createPaymentForm"
              disabled={creating || !createForm.invoiceId || createForm.amountCents <= 0}
              className="px-5 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {creating ? "Recording..." : "Record Payment"}
            </button>
          </div>
        }
      >
        <form id="createPaymentForm" onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Invoice *
            </label>
            <SearchableSelect
              options={invoices.map((inv: any) => ({
                value: inv._id,
                label: `${inv.invoiceNumber}  -  ${formatCurrency(inv.balanceDueCents)} due`,
              }))}
              value={createForm.invoiceId || null}
              onChange={(val) => setCreateForm({ ...createForm, invoiceId: val || "" })}
              placeholder="Select invoice"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Amount ($) *
            </label>
            <input
              type="number"
              required
              min="0.01"
              step="0.01"
              value={createForm.amountCents ? createForm.amountCents / 100 : ""}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  amountCents: Math.round(parseFloat(e.target.value || "0") * 100),
                })
              }
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="1000.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Payment Source *
            </label>
            <SearchableSelect
              options={[
                { value: "cheque", label: "Cheque" },
                { value: "eft", label: "EFT" },
                { value: "cash", label: "Cash" },
                { value: "stripe", label: "Stripe" },
                { value: "trust_transfer", label: "Trust Transfer" },
              ]}
              value={createForm.source}
              onChange={(val) => setCreateForm({ ...createForm, source: val || "cheque" })}
              placeholder="Select source"
            />
          </div>

          {createForm.source === "cheque" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Cheque Number
              </label>
              <input
                type="text"
                value={createForm.chequeNumber}
                onChange={(e) =>
                  setCreateForm({ ...createForm, chequeNumber: e.target.value })
                }
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          )}

          {createForm.source === "eft" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                EFT Reference
              </label>
              <input
                type="text"
                value={createForm.eftReference}
                onChange={(e) =>
                  setCreateForm({ ...createForm, eftReference: e.target.value })
                }
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={createForm.notes}
              onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
              rows={2}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          {/* Receipt Attachment */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Attach Receipt</label>
            <input
              ref={receiptFileRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
            />
            {receiptFile ? (
              <div className="flex items-center gap-3 p-3 bg-brand-light border border-brand-light rounded-xl">
                <Paperclip className="h-4 w-4 text-brand-accent flex-shrink-0" />
                <span className="text-sm text-brand truncate flex-1">{receiptFile.name}</span>
                <button
                  type="button"
                  onClick={() => { setReceiptFile(null); if (receiptFileRef.current) receiptFileRef.current.value = ""; }}
                  className="p-1 rounded hover:bg-brand-light text-brand-accent"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => receiptFileRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-xl text-sm text-slate-500 hover:border-brand-accent hover:text-brand-accent transition-colors"
              >
                <Upload className="h-4 w-4" />
                Upload receipt image or PDF
              </button>
            )}
          </div>
        </form>
      </PremiumModal>
    </div>
  );
};

export default PaymentsTab;
